import type { ReactNode } from 'react'
import { Button, Input, Label, ListBox, ListBoxItem, SearchField, Select } from '@heroui/react'

import { DownloadIcon } from '@/components/ui/icons'
import type { SelectOption } from '@/components/ui/form-fields'

import type { CustomRange } from '../report-range'

interface ReportFiltersProps {
  /** The first trading day in the period, `YYYY-MM-DD`. */
  from: string
  /** The last trading day in the period, `YYYY-MM-DD` — the whole of it counts. */
  to: string
  onDatesChange: (range: CustomRange) => void
  /**
   * Narrows the rows on screen to the ones that mention a word — an invoice, a
   * customer, an item. Omitted on a report with nothing worth looking up.
   */
  search?: ReportSearchProps
  /** The report's own fields — a channel, a category — between the search and the dates. */
  children?: ReactNode
  /** Omitted while a report has nothing to export yet. */
  onExport?: () => void
  /** What the export button says. */
  exportLabel?: string
  /** A server-built export is on its way. */
  isExporting?: boolean
}

/**
 * The row every report wears: what to look for, which slice, which days, and a
 * way out to a spreadsheet.
 *
 * One component rather than one per report, because the whole value of a filter
 * row is that it is in the same place and behaves the same way on each one. The
 * outlet is NOT here: it is the module bar's Location, chosen once for every
 * report and the summary, so walking from one report to the next never quietly
 * changes which outlets the figures cover.
 *
 * Every field carries its label above it, and the row wraps as whole fields —
 * a label is never separated from its control, and Export stays at the end of
 * whichever line it lands on.
 */
export function ReportFilters({
  from,
  to,
  onDatesChange,
  search,
  children,
  onExport,
  exportLabel = 'Export',
  isExporting = false,
}: ReportFiltersProps) {
  return (
    <ReportFilterRow>
      {search && <ReportSearch {...search} />}
      {children}
      {/* Each end is bounded by the other, so the native calendar will not offer
          an End Date before the Start Date. A typed one is still swapped rather
          than refused — see `resolveTradingDays`. */}
      <ReportDateField
        label="Start Date"
        value={from}
        max={to}
        onChange={(next) => onDatesChange({ from: next, to })}
      />
      <ReportDateField
        label="End Date"
        value={to}
        min={from}
        onChange={(next) => onDatesChange({ from, to: next })}
      />
      {onExport && (
        <ReportExportButton onPress={onExport} isExporting={isExporting} label={exportLabel} />
      )}
    </ReportFilterRow>
  )
}

/** The row itself, for a report whose fields are not a period — Stock on hand's "as of". */
export function ReportFilterRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-x-4 gap-y-3">{children}</div>
}

/* -------------------------------------------------------------------------- */
/* Fields                                                                     */
/* -------------------------------------------------------------------------- */

/** The label over every field in the row. */
const FIELD_LABEL = 'text-foreground text-sm font-semibold'

/**
 * Wide enough for "All categories" or a date, and the same for every field so
 * the row reads as a grid. The design's 200px is for a 1248px column; this is
 * the same share of the app's narrower one, which is what keeps a report with
 * two selects, the search, both dates and Export on one line.
 */
const FIELD_WIDTH = 'w-full sm:w-44'

interface ReportSearchProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
}

/**
 * The search box, which takes whatever width the row has left.
 *
 * It filters what is already on screen rather than asking the server again: a
 * report is read whole for its period, so looking one invoice up in it is a
 * question about rows the browser already holds.
 */
export function ReportSearch({ value, onChange, placeholder }: ReportSearchProps) {
  return (
    <SearchField value={value} onChange={onChange} className="flex min-w-40 flex-1 flex-col gap-1">
      <Label className={FIELD_LABEL}>Search</Label>
      <SearchField.Group className="h-10 w-full">
        <SearchField.SearchIcon />
        <SearchField.Input placeholder={placeholder} />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  )
}

interface ReportSelectProps {
  label: string
  options: SelectOption[]
  value: string | null
  onChange: (value: string | null) => void
  /** The "everything" option — "All categories". Omit when a choice is required. */
  allLabel?: string
  isDisabled?: boolean
}

/** A key React Aria can hold that stands for "no filter". */
const ALL = 'all'

/**
 * A dropdown in the filter row, labelled above like every field beside it.
 *
 * `FilterSelect` is the same control with its label beside the trigger, for the
 * module bar; inside this row the labels stack, so the two cannot share markup.
 */
export function ReportSelect({
  label,
  options,
  value,
  onChange,
  allLabel,
  isDisabled,
}: ReportSelectProps) {
  const items = [...(allLabel ? [{ value: ALL, label: allLabel }] : []), ...options]

  return (
    <Select
      selectedKey={value ?? (allLabel ? ALL : null)}
      onSelectionChange={(key) => onChange(key === ALL || key === null ? null : String(key))}
      isDisabled={isDisabled || items.length === 0}
      className={`flex flex-col gap-1 ${FIELD_WIDTH}`}
    >
      <Label className={FIELD_LABEL}>{label}</Label>
      <Select.Trigger className="h-10">
        <Select.Value className="truncate" />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {items.map((option) => (
            <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

interface ReportDateFieldProps {
  label: string
  /** `YYYY-MM-DD`, or empty. */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  /** Shown under the field's own row — "Live" on Stock on hand. */
  action?: ReactNode
}

/**
 * A native date field in the field's own clothes.
 *
 * Native because a date picker is one of the few controls every platform already
 * has a better version of than a web app can build — the phone keyboard, the
 * desktop calendar and the locale format all come free, and a manager entering
 * last month's window does it with the control they already know.
 *
 * An empty value is ignored rather than passed up: clearing the field with the
 * keyboard on the way to typing a new date would otherwise ask the server for a
 * period with no end.
 */
export function ReportDateField({
  label,
  value,
  onChange,
  min,
  max,
  action,
}: ReportDateFieldProps) {
  return (
    <label className={`flex flex-col gap-1 ${FIELD_WIDTH}`}>
      <span className="flex items-center justify-between gap-2">
        <span className={FIELD_LABEL}>{label}</span>
        {action}
      </span>
      <Input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          if (event.target.value) onChange(event.target.value)
        }}
        className="h-10 w-full tabular-nums"
      />
    </label>
  )
}

/**
 * Export, at the end of the row.
 *
 * A plain pill rather than the brand one: the row's job is to set the period,
 * and the loudest thing on it should not be the one action that leaves the
 * screen. `ms-auto` holds it to the end of whichever line it wraps onto.
 */
export function ReportExportButton({
  onPress,
  isExporting = false,
  label = 'Export',
}: {
  onPress: () => void
  isExporting?: boolean
  label?: string
}) {
  return (
    <Button
      variant="ghost"
      onPress={onPress}
      isDisabled={isExporting}
      className="bg-surface text-foreground hover:bg-surface-secondary ms-auto h-10 rounded-full px-3 font-semibold shadow-field"
    >
      <DownloadIcon width={16} height={16} />
      {isExporting ? 'Preparing…' : label}
    </Button>
  )
}
